import React, { useState, useContext, ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  Alert,
  Box,
  Typography,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  CircularProgress,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  ButtonBase,
  Tooltip
} from '@mui/material';

import Slider from '@mui/material/Slider';
// Using TextField with time input instead of MUI TimePicker
import { logEvent } from '../../services/analytics';
import { AppContext, Line, Fleet, Polygon, Layer, Shape, Point } from '../../context/context';
import { Vehicle } from '../map/components/FleetMenu';
import { v4 as uuidv4 } from 'uuid';
import { pointInPolygon, randomPointInPolygon, weightedRandom, weightedRandomTime, makePoint } from '../map/components/generate-rides/utils';

import Snackbar from '@mui/material/Snackbar';
import { DEFAULT_SIMULATION_DATE } from '../../config/constants';
import { createWallTimeDate, formatDateAsWallTime } from '../../utils/dateTimeUtils';
import {
  DEFAULT_HUB_DEMAND_SPLIT,
  pickRandomHub,
  selectTripKind
} from './populationAnalysisHubDemand';
import {
  frostedDrawerPaperSx,
  frostedDrawerHeaderSx,
} from '../../theme/componentStyles';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../utils/iconFallbacks';
import { opsColors, opsElevation } from '../../theme/opsTokens';

interface PopulationAnalysisDrawerProps {
  open: boolean;
  onClose: () => void;
  // Note: This should only contain polygons, but TypeScript can't narrow the type after filtering
  polygons: (Shape & { _layerName?: string })[];
  selectedPolygonId: string | null;
  onPolygonChange: (id: string) => void;
  onGetPopulationData: (polygonId: string) => Promise<void>;
  onValidatePopulationPolygon?: (polygonId: string) => string | null;
  transitError?: string | null;
  transitLoading?: boolean;
  setRecentRideLayerId: (layerId: string) => void;
  setRecentFleetId: (fleetId: string) => void;
}

// Add snackbar state with correct type
type SnackbarState = { open: boolean; message: string; severity: 'success' | 'info' | 'warning' | 'error'; icon: ReactNode };

// Shared layer name for all Population Analysis generated output. Generating multiple
// times appends to this single layer instead of cluttering the Layer Panel with one
// new layer per click.
const GENERATED_LAYER_NAME = 'Population Analysis - Generated';
const GENERATED_KEY_STOP_ID_PREFIX = 'pa-key-stop-';
const GENERATED_KEY_POI_ID_PREFIX = 'pa-key-poi-';

const KEY_POI_TERMS = ['school', 'hospital', 'supermarket', 'library'];
const KEY_POI_ICONS = new Set(['school', 'hospital', 'store']);
type KeyLocationType = 'transit_stop' | 'health_facility' | 'school' | 'supermarket' | 'library';
const KEY_LOCATION_DEMAND_SPLIT: Record<KeyLocationType, number> = {
  transit_stop: 0.30,
  health_facility: 0.30,
  school: 0.10,
  supermarket: 0.20,
  library: 0.10,
};

function classifyKeyLocationType(point: Point): KeyLocationType | null {
  if (point.creationSource === 'transit-osm-stop') return 'transit_stop';
  const lowerName = point.name.toLowerCase();
  const lowerDesc = point.description.toLowerCase();
  const text = `${lowerName} ${lowerDesc}`;
  if (text.includes('hospital') || point.icon === 'hospital') return 'health_facility';
  if (text.includes('school') || point.icon === 'school') return 'school';
  if (text.includes('supermarket') || point.icon === 'store') return 'supermarket';
  if (text.includes('library')) return 'library';
  return null;
}

function pickWeightedKeyLocationCoordinate(
  buckets: Record<KeyLocationType, [number, number][]>
): [number, number] | null {
  const availableTypes = (Object.keys(KEY_LOCATION_DEMAND_SPLIT) as KeyLocationType[]).filter(
    (type) => buckets[type].length > 0
  );
  if (availableTypes.length === 0) return null;
  const totalWeight = availableTypes.reduce((sum, type) => sum + KEY_LOCATION_DEMAND_SPLIT[type], 0);
  if (totalWeight <= 0) return null;
  const r = Math.random();
  let cumulative = 0;
  let selectedType = availableTypes[availableTypes.length - 1];
  for (const type of availableTypes) {
    cumulative += KEY_LOCATION_DEMAND_SPLIT[type] / totalWeight;
    if (r <= cumulative) {
      selectedType = type;
      break;
    }
  }
  return pickRandomHub(buckets[selectedType]);
}

const PopulationAnalysisDrawer: React.FC<PopulationAnalysisDrawerProps> = ({
  open,
  onClose,
  polygons,
  selectedPolygonId,
  onPolygonChange,
  onGetPopulationData,
  onValidatePopulationPolygon,
  transitError,
  transitLoading = false,
  setRecentRideLayerId,
  setRecentFleetId
}) => {
  const { t } = useTranslation('mapDialogs');
  // Filter to ensure we only work with polygons (runtime safety check)
  const polygonShapes = polygons.filter((p): p is Polygon & { _layerName?: string } => p.type === 'polygon');
  const selectedPolygon = polygonShapes.find(p => p.id === selectedPolygonId);
  const hasPolygons = polygonShapes.length > 0;
  const [isPolygonSelectOpen, setIsPolygonSelectOpen] = useState(false);
  const [flexibility, setFlexibility] = useState(50); // default medium
  const [demandPct, setDemandPct] = useState(0.25); // default medium
  const [loadingPop, setLoadingPop] = useState(false);
  const context = useContext(AppContext);
  const global = context?.global;

  // Advanced Settings State
  const [advancedSettingsExpanded, setAdvancedSettingsExpanded] = useState(false);
  const [startTime, setStartTime] = useState<Date>(
    () => createWallTimeDate(DEFAULT_SIMULATION_DATE, '06:00')
  );
  const [endTime, setEndTime] = useState<Date>(
    () => createWallTimeDate(DEFAULT_SIMULATION_DATE, '20:00')
  );
  const [peakinessProfile, setPeakinessProfile] = useState('');

  // Add snackbar state with correct type
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'success', icon: null });
  const [populationPolygonValidationError, setPopulationPolygonValidationError] = useState<string | null>(null);
  // Which generation action (if any) is currently running. Trip generation is CPU-bound
  // and synchronous, so the UI needs an explicit "in progress" state to show a spinner.
  const [generating, setGenerating] = useState<null | 'trips' | 'fleet' | 'both'>(null);

  // Prevent orphaned Select dropdown menus when the drawer is collapsed/minimized.
  useEffect(() => {
    if ((!open || !hasPolygons) && isPolygonSelectOpen) {
      setIsPolygonSelectOpen(false);
    }
  }, [open, hasPolygons, isPolygonSelectOpen]);

  // Track analysis initiation when drawer opens
  React.useEffect(() => {
    if (open) {
      logEvent({
        category: 'Analysis',
        action: 'PopulationAnalysisOpened',
        label: 'Population Analysis Drawer Opened',
        polygonCount: polygons.length,
        hasSelectedPolygon: !!selectedPolygonId
      });
    }
  }, [open, polygons.length, selectedPolygonId]);

  // Track analysis completion once per population fetch (not on every slider tweak).
  // Keyed by polygon + total population so the event fires when fresh data arrives.
  const completedKeyRef = useRef<string | null>(null);
  const completionKey =
    selectedPolygonId && selectedPolygon?.totalPopulation != null
      ? `${selectedPolygonId}:${selectedPolygon.totalPopulation}`
      : null;
  React.useEffect(() => {
    if (!completionKey || completedKeyRef.current === completionKey) return;
    completedKeyRef.current = completionKey;
    const totalPop = selectedPolygon?.totalPopulation || 0;
    const dailyTrips = totalPop * demandPct / 30;
    const vehiclesNeeded = dailyTrips / flexibility;
    logEvent({
      category: 'Analysis',
      action: 'PopulationAnalysisCompleted',
      label: 'Population Analysis Calculations Completed',
      polygonId: selectedPolygonId || 'none',
      polygonName: selectedPolygon?.name || 'Unnamed',
      totalPopulation: selectedPolygon?.totalPopulation,
      coveredPopulation: selectedPolygon?.coveredPopulation || 0,
      desertPopulation: selectedPolygon?.desertPopulation || 0,
      flexibility,
      demandPercentage: Math.round(demandPct * 100),
      dailyTrips,
      vehiclesNeeded
    });
    // Only `completionKey` should re-trigger this; slider values are read for the
    // log payload but intentionally not in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completionKey]);

  // Handle polygon selection with analytics
  const handlePolygonChange = (polygonId: string) => {
    setPopulationPolygonValidationError(null);
    onPolygonChange(polygonId);
    
    // Track polygon selection
    const selectedPoly = polygonShapes.find(p => p.id === polygonId);
    logEvent({
      category: 'Analysis',
      action: 'PolygonSelected',
      label: 'Polygon Selected for Analysis',
      polygonId: polygonId,
      polygonName: selectedPoly?.name || 'Unnamed',
      layerName: selectedPoly?._layerName || 'Unknown',
      hasPopulationData: selectedPoly?.totalPopulation != null
    });
  };

  // Slider marks (now with very low and very high)
  const flexibilityStops = [30, 40, 50, 60, 70];
  const flexibilityMarks = [
    { value: 30, label: <><span>{t('populationAnalysis.veryLow').split(' ')[0]}</span><br /><span>{t('populationAnalysis.veryLow').split(' ').slice(1).join(' ')}</span></> },
    { value: 40, label: t('populationAnalysis.low') },
    { value: 50, label: t('populationAnalysis.medium') },
    { value: 60, label: t('populationAnalysis.high') },
    { value: 70, label: <><span>{t('populationAnalysis.veryHigh').split(' ')[0]}</span><br /><span>{t('populationAnalysis.veryHigh').split(' ').slice(1).join(' ')}</span></> },
  ];
  const demandStops = [0.10, 0.15, 0.25, 0.35, 0.40];
  const demandMarks = [
    { value: 0.10, label: <><span>{t('populationAnalysis.veryLow').split(' ')[0]}</span><br /><span>{t('populationAnalysis.veryLow').split(' ').slice(1).join(' ')}</span></> },
    { value: 0.15, label: t('populationAnalysis.low') },
    { value: 0.25, label: t('populationAnalysis.medium') },
    { value: 0.35, label: t('populationAnalysis.high') },
    { value: 0.40, label: <><span>{t('populationAnalysis.veryHigh').split(' ')[0]}</span><br /><span>{t('populationAnalysis.veryHigh').split(' ').slice(1).join(' ')}</span></> },
  ];

  // Sticky stop logic
  function snapToStop(value: number, stops: number[], threshold: number) {
    for (const stop of stops) {
      if (Math.abs(value - stop) < threshold) return stop;
    }
    return value;
  }

  // Calculations
  const totalPop = selectedPolygon?.totalPopulation || 0;
  const dailyTripsRaw = totalPop * demandPct / 30;
  const vehiclesNeededRaw = dailyTripsRaw / flexibility;

  // Parses display strings like "120" or "100–140" into a single integer
  // (midpoint for ranges). Used to convert UI labels into a count for generation.
  function parseDisplayCount(display: string): number {
    if (display.includes('–')) {
      const [min, max] = display.split('–').map(s => parseInt(s.trim(), 10));
      return Math.round((min + max) / 2);
    }
    return parseInt(display, 10) || 0;
  }

  // Helper for daily trips range (10% band, rounded to nearest 10)
  function getTripRange(trips: number) {
    const band = Math.max(1, Math.round(trips * 0.1));
    const min = Math.floor((trips - band) / 10) * 10;
    const max = Math.ceil((trips + band) / 10) * 10;
    return min === max ? `${min}` : `${min}–${max}`;
  }

  // Helper for vehicles needed (range if decimal between 0.3 and 0.7)
  function getVehicleDisplay(vehicles: number) {
    const floor = Math.floor(vehicles);
    const ceil = Math.ceil(vehicles);
    const decimal = vehicles - floor;
    if (decimal >= 0.3 && decimal <= 0.7) {
      return `${floor}–${ceil}`;
    }
    return `${Math.round(vehicles)}`;
  }

  // Helper functions for analytics
  const getFlexibilityLevel = (value: number): string => {
    if (value <= 35) return 'Very Low';
    if (value <= 45) return 'Low';
    if (value <= 55) return 'Medium';
    if (value <= 65) return 'High';
    return 'Very High';
  };

  const getDemandLevel = (value: number): string => {
    if (value <= 0.12) return 'Very Low';
    if (value <= 0.20) return 'Low';
    if (value <= 0.30) return 'Medium';
    if (value <= 0.37) return 'High';
    return 'Very High';
  };

  // Peakiness Profile Presets
  const peakinessProfiles = {
    commuter: {
      name: t('populationAnalysis.commuterName'),
      description: t('populationAnalysis.commuterDescription'),
      icon: ICON_FALLBACKS.WorkOutline,
      peaks: [
        { peak_time: '08:00', effect: 4, width_left: 3, width_right: 3 },
        { peak_time: '17:30', effect: 4, width_left: 1, width_right: 3 }
      ]
    },
    school: {
      name: t('populationAnalysis.schoolName'),
      description: t('populationAnalysis.schoolDescription'),
      icon: ICON_FALLBACKS.School,
      peaks: [
        { peak_time: '08:00', effect: 4, width_left: 2, width_right: 1 },
        { peak_time: '15:30', effect: 4, width_left: 2, width_right: 1 }
      ]
    },
    midday: {
      name: t('populationAnalysis.middayName'),
      description: t('populationAnalysis.middayDescription'),
      icon: ICON_FALLBACKS.WbSunny,
      peaks: [
        { peak_time: '12:30', effect: 2.0, width_left: 3, width_right: 3 }
      ]
    }
  };

  const dailyTripsDisplay = totalPop === 0 ? '0' : getTripRange(dailyTripsRaw);
  const vehiclesNeededDisplay = getVehicleDisplay(vehiclesNeededRaw);
  const sectionCardSx = {
    p: 2,
    borderRadius: '14px',
    border: '1px solid',
    borderColor: 'surface.borderSubtle',
    backgroundColor: 'surface.primary',
    boxShadow: 1,
  };

  // Helper: Generate a new trip layer (stubbed trip generation for now).
  // `silent` suppresses this helper's own snackbars so callers (e.g. handleGenerateBoth)
  // can own a single combined notification.
  const handleGenerateTrips = (silent = false) => {
    if (!global || !selectedPolygon) {
      if (!silent) {
        setSnackbar({
          open: true,
          message: t('populationAnalysis.errorMissingContextTrips'),
          severity: 'error',
          icon: <Icon size={20} name="triangle-exclamation" style={{ marginRight: 8, color: '#d32f2f' }} />
        });
      }
      return undefined;
    }
    
    try {
    const tripsCenter = parseDisplayCount(dailyTripsDisplay);
    if (!tripsCenter || tripsCenter <= 0) return undefined;

    // Get polygon coordinates for random point generation
    const polygonCoords: [number, number][] = selectedPolygon.coordinates[0];

    // --- Use advanced settings for time window and peak profile ---
    // startTime/endTime are already Dates anchored to DEFAULT_SIMULATION_DATE,
    // so we can use them directly as the bounds for weightedRandomTime.
    const startDateObj = startTime;
    const endDateObj = endTime;
    // Use selected peakiness profile
    const selectedProfile = peakinessProfile ? peakinessProfiles[peakinessProfile as keyof typeof peakinessProfiles] : null;
    const peaks = selectedProfile ? selectedProfile.peaks : [];
    const rideTypes = [{ type: 'arrival_fixed', probability: 1 }];
    const numberOfPassengers = [
      { number_of_passengers: 1, probability: 0.7 },
      { number_of_passengers: 2, probability: 0.2 },
      { number_of_passengers: 3, probability: 0.07 },
      { number_of_passengers: 4, probability: 0.03 }
    ];
    const passengerTypes = [
      { passenger_type: 'standard', probability: 0.95 },
      { passenger_type: 'wheelchair', probability: 0.05 }
    ];

    const points: Point[] = [];
    const lines: Line[] = [];
    const polygonRing = selectedPolygon.coordinates[0];
    const polygonPoints = global.mapLayers
      .flatMap((layer) => layer.shapes)
      .filter((shape): shape is Point => shape.type === 'point')
      .filter((shape) => {
        if (shape.hidden) return false;
        return pointInPolygon(shape.coordinates, polygonRing);
      });
    const keyTransitStops = polygonPoints.filter((shape) =>
      shape.creationSource === 'transit-osm-stop' ||
      shape.id.startsWith('osm-') ||
      shape.name.toLowerCase().includes('osm')
    );
    const keyPointsOfInterest = polygonPoints.filter((shape) => {
      if (keyTransitStops.some((stop) => stop.id === shape.id)) return false;
      const lowerName = shape.name.toLowerCase();
      const matchesName = KEY_POI_TERMS.some((term) => lowerName.includes(term));
      const matchesIcon = KEY_POI_ICONS.has(shape.icon);
      return matchesName || matchesIcon;
    });
    const keyLocationCoordinates: [number, number][] = [
      ...keyTransitStops.map((shape) => shape.coordinates),
      ...keyPointsOfInterest.map((shape) => shape.coordinates),
    ];
    const keyLocationBuckets: Record<KeyLocationType, [number, number][]> = {
      transit_stop: [],
      health_facility: [],
      school: [],
      supermarket: [],
      library: [],
    };
    for (const stop of keyTransitStops) keyLocationBuckets.transit_stop.push(stop.coordinates);
    for (const poi of keyPointsOfInterest) {
      const kind = classifyKeyLocationType(poi);
      if (kind && kind !== 'transit_stop') keyLocationBuckets[kind].push(poi.coordinates);
    }
    const randomKeyLocationPoint = () =>
      pickWeightedKeyLocationCoordinate(keyLocationBuckets) ?? pickRandomHub(keyLocationCoordinates);

    for (let i = 0; i < tripsCenter; i++) {
      const tripKind = keyLocationCoordinates.length > 0
        ? selectTripKind({
          ...DEFAULT_HUB_DEMAND_SPLIT,
          pointToHub: DEFAULT_HUB_DEMAND_SPLIT.pointToHub + DEFAULT_HUB_DEMAND_SPLIT.pointToPoint,
          pointToPoint: 0,
        })
        : 'point_to_point';
      const randomPolygonPoint = () => randomPointInPolygon(polygonCoords);
      const randomHubPoint = () => randomKeyLocationPoint();
      const pointToHubAsOrigin = Math.random() < 0.5;

      let originCoords: [number, number];
      let destCoords: [number, number];

      if (tripKind === 'hub_to_hub' && keyLocationCoordinates.length > 1) {
        originCoords = randomHubPoint();
        destCoords = randomHubPoint();
      } else if (tripKind === 'point_to_hub' && keyLocationCoordinates.length > 0) {
        if (pointToHubAsOrigin) {
          originCoords = randomPolygonPoint();
          destCoords = randomHubPoint();
        } else {
          originCoords = randomHubPoint();
          destCoords = randomPolygonPoint();
        }
      } else {
        originCoords = randomPolygonPoint();
        destCoords = randomPolygonPoint();
      }

      const origin = makePoint(originCoords, t('populationAnalysis.originN', { n: i + 1 }), '#FF0000');
      const destination = makePoint(destCoords, t('populationAnalysis.destinationN', { n: i + 1 }), '#00FF00');
      origin.id = uuidv4();
      destination.id = uuidv4();
      points.push(origin, destination);

      // Request time (weighted by commuter peaks)
      const requestTime = weightedRandomTime(startDateObj, endDateObj, peaks);
      // Ride type
      const rideType = weightedRandom(rideTypes, rideTypes.map(r => r.probability)).type;
      // Number of passengers
      const numPassengers = weightedRandom(numberOfPassengers, numberOfPassengers.map(g => g.probability)).number_of_passengers;
      let standardSeat = 0;
      let wheelchair = 0;
      for (let j = 0; j < numPassengers; j++) {
        const type = weightedRandom(passengerTypes, passengerTypes.map(p => p.probability)).passenger_type.toLowerCase();
        if (type.includes('wheelchair')) {
          wheelchair++;
        } else {
          standardSeat++;
        }
      }
      // Line (trip)
      const rideId = `Trip-${i + 1}`;
      const userId = `user-${i + 1}`;
      
      const requestTimeString = requestTime instanceof Date
        ? formatDateAsWallTime(requestTime)
        : requestTime;
      
      const line: Line & { originPointId: string; destPointId: string } = {
        id: uuidv4(),
        type: 'line',
        name: rideId,
        ride_id: rideId,
        user_id: userId,
        colour: '#0000FF',
        description: '',
        opacity: '1',
        borderColour: '#0000FF',
        borderWidth: 2,
        isLocked: false,
        coordinates: [origin.coordinates, destination.coordinates],
        arrowStyle: 'arrow',
        time: requestTimeString,
        numPassengers,
        standard_seat: standardSeat,
        wheelchair: wheelchair,
        request_type: rideType,
        originPointId: origin.id,
        destPointId: destination.id,
      };
      lines.push(line);
    }

    const keyLocationStopsToAdd: Point[] = keyTransitStops.map((stop, idx) => ({
      ...stop,
      id: `${GENERATED_KEY_STOP_ID_PREFIX}${stop.id}`,
      name: stop.name || t('populationAnalysis.keyTransitStopN', { n: idx + 1 }),
      hidden: false,
    }));
    const keyLocationPoisToAdd: Point[] = keyPointsOfInterest.map((poi, idx) => ({
      ...poi,
      id: `${GENERATED_KEY_POI_ID_PREFIX}${poi.id}`,
      name: poi.name || t('populationAnalysis.keyPoiN', { n: idx + 1 }),
      hidden: false,
    }));
    // Append into the shared "Population Analysis - Generated" layer if it already
    // exists; otherwise create it. This avoids piling up one new layer per click.
    const existingLayer = global.mapLayers.find((l: Layer) => l.layerName === GENERATED_LAYER_NAME);
    const isAppend = !!existingLayer;
    const targetLayerId = existingLayer
      ? existingLayer.layerId
      : (global.mapLayers.length > 0 ? Math.max(...global.mapLayers.map((l: Layer) => l.layerId)) + 1 : 0);

    global.setLayers(prev => {
      const idx = prev.findIndex(l => l.layerName === GENERATED_LAYER_NAME);
      const appendToLayer = (layer: Layer) => {
        const existingIds = new Set(layer.shapes.map((shape) => shape.id));
        const keyStops = keyLocationStopsToAdd.filter((shape) => !existingIds.has(shape.id));
        const keyPois = keyLocationPoisToAdd.filter((shape) => !existingIds.has(shape.id));
        return { ...layer, shapes: [...layer.shapes, ...keyStops, ...keyPois, ...points, ...lines] };
      };
      if (idx >= 0) {
        return prev.map((l, i) => i === idx ? appendToLayer(l) : l);
      }
      return [...prev, {
        layerId: targetLayerId,
        layerName: GENERATED_LAYER_NAME,
        hidden: false,
        shapes: [...keyLocationStopsToAdd, ...keyLocationPoisToAdd, ...points, ...lines]
      }];
    });
    global.setActiveLayerId(targetLayerId);
    // Log analytics event
    logEvent({
      category: 'PopulationAnalysis',
      action: 'TripsGenerated',
      label: 'Auto-generated trips from population analysis',
      polygonId: selectedPolygon.id,
      polygonName: selectedPolygon.name || 'Unknown',
      numberOfRides: tripsCenter,
      generatedPoints: points.length,
      generatedLines: lines.length,
      keyTransitStopCount: keyTransitStops.length,
      keyPoiCount: keyPointsOfInterest.length,
      tripsAnchoredToKeyLocations: keyLocationCoordinates.length > 0,
      newLayerId: targetLayerId,
      newLayerName: GENERATED_LAYER_NAME,
      appendedToExistingLayer: isAppend
    });
    if (!silent) {
      setSnackbar({
        open: true,
        message: isAppend
          ? t('populationAnalysis.addedTripsToLayerWithKeyLocations', { count: tripsCenter, layer: GENERATED_LAYER_NAME, stops: keyTransitStops.length, pois: keyPointsOfInterest.length })
          : t('populationAnalysis.createdLayerWithTripsAndKeyLocations', { count: tripsCenter, layer: GENERATED_LAYER_NAME, stops: keyTransitStops.length, pois: keyPointsOfInterest.length }),
        severity: 'success',
        icon: <Icon size={20} d={ICON_FALLBACKS.AltRoute} style={{ marginRight: 8, color: '#2e7d32' }} />
      });
    }
    setRecentRideLayerId(targetLayerId.toString());
    return targetLayerId;
    } catch (error) {
      console.error('Error generating trips:', error);
      if (!silent) {
        setSnackbar({
          open: true,
          message: t('populationAnalysis.tripsGenerationFailed', { message: error instanceof Error ? error.message : t('populationAnalysis.unknownError') }),
          severity: 'error',
          icon: <Icon size={20} name="triangle-exclamation" style={{ marginRight: 8, color: '#d32f2f' }} />
        });
      }
      return undefined;
    }
  };

  // Helper: Calculate centroid of a polygon
  function getPolygonCentroid(polygon: Polygon): { lat: number, lng: number } {
    // polygon.coordinates: [ [ [lat, lng], ... ] ] (outer ring)
    const coords = polygon.coordinates[0];
    let x = 0, y = 0, area = 0;
    for (let i = 0, len = coords.length, j = len - 1; i < len; j = i++) {
      const [lat1, lng1] = coords[j];
      const [lat2, lng2] = coords[i];
      const f = lat1 * lng2 - lat2 * lng1;
      x += (lat1 + lat2) * f;
      y += (lng1 + lng2) * f;
      area += f;
    }
    area = area / 2;
    if (area === 0) return { lat: coords[0][0], lng: coords[0][1] }; // fallback
    x = x / (6 * area);
    y = y / (6 * area);
    return { lat: x, lng: y };
  }

  // Helper: Generate a hidden centroid point if not present.
  // Looks up by deterministic id (`zone-center-${polygon.id}`) instead of coordinate
  // equality to avoid float-drift creating duplicate centroids on every fleet generation.
  const ensureZoneCenterPoint = (polygon: Polygon, global: any): string => {
    const pointId = `zone-center-${polygon.id}`;
    const allPoints = global.mapLayers.flatMap((layer: Layer) =>
      layer.shapes.filter((s: Shape) => s.type === 'point')
    );
    const existing = allPoints.find((p: Shape) => p.id === pointId);
    if (existing) return existing.id;
    const centroid = getPolygonCentroid(polygon);
    const newPoint: Point = {
      id: pointId,
      type: 'point',
      name: t('populationAnalysis.zoneCenter'),
      description: t('populationAnalysis.zoneCenterDescription'),
      colour: '#1976d2',
      icon: 'star',
      isLocked: true,
      coordinates: [centroid.lat, centroid.lng],
      hidden: true
    };
    // Prefer the shared Population Analysis layer; fall back to the polygon's layer,
    // then the first layer. If the shared layer doesn't exist yet, create it.
    global.setLayers((prev: Layer[]) => {
      const sharedIdx = prev.findIndex((l: Layer) => l.layerName === GENERATED_LAYER_NAME);
      if (sharedIdx >= 0) {
        return prev.map((layer: Layer, idx: number) =>
          idx === sharedIdx ? { ...layer, shapes: [...layer.shapes, newPoint] } : layer
        );
      }
      // Create the shared layer with the centroid as its first shape
      const nextLayerId = prev.length > 0 ? Math.max(...prev.map((l: Layer) => l.layerId)) + 1 : 0;
      return [...prev, { layerId: nextLayerId, layerName: GENERATED_LAYER_NAME, hidden: false, shapes: [newPoint] }];
    });
    return pointId;
  };

  // Helper: Generate a new fleet. `silent` suppresses this helper's own snackbars.
  const handleGenerateFleet = (silent = false) => {
    if (!global || !selectedPolygon) {
      if (!silent) {
        setSnackbar({
          open: true,
          message: t('populationAnalysis.errorMissingContextFleet'),
          severity: 'error',
          icon: <Icon size={20} name="triangle-exclamation" style={{ marginRight: 8, color: '#d32f2f' }} />
        });
      }
      return undefined;
    }
    
    try {
    const vehiclesNeeded = parseDisplayCount(vehiclesNeededDisplay);
    if (!vehiclesNeeded || vehiclesNeeded <= 0) return undefined;

    // Use advanced settings for operating period
    const startTimeStr = startTime.toTimeString().slice(0, 5);
    const endTimeStr = endTime.toTimeString().slice(0, 5);
    const defaultOperatingPeriod = {
      start: startTimeStr,
      end: endTimeStr,
      breaks: []
    };

    // Ensure a hidden centroid point exists and get its id
    const centroidPointId = ensureZoneCenterPoint(selectedPolygon, global);

    // Generate vehicles
    const vehicles: Vehicle[] = Array.from({ length: vehiclesNeeded }).map((_, i) => ({
      id: `auto-vehicle-${selectedPolygon.id}-${i}`,
      name: t('populationAnalysis.autoVehicle', { n: i + 1 }),
      standardSeats: 10,
      wheelchairSeats: 1,
      startLocation: centroidPointId,
      endLocation: centroidPointId,
      operatingHours: [defaultOperatingPeriod],
      numberOfVehicles: 1
    }));

    // Create a new fleet object
    const newFleet: Fleet = {
      id: `auto-fleet-${selectedPolygon.id}-${Date.now()}`,
      name: t('populationAnalysis.autoFleet', { name: selectedPolygon.name || selectedPolygon.id }),
      vehicles
    };
    global.setFleets(prev => [...prev, newFleet]);
    if (!silent) {
      setSnackbar({
        open: true,
        message: t('populationAnalysis.generatedVehicles', { count: vehiclesNeeded, name: newFleet.name }),
        severity: 'success',
        icon: <Icon size={20} name="bus" style={{ marginRight: 8, color: '#2e7d32' }} />
      });
    }
    setRecentFleetId(newFleet.id);
    return newFleet.id;
    } catch (error) {
      console.error('Error generating fleet:', error);
      if (!silent) {
        setSnackbar({
          open: true,
          message: t('populationAnalysis.fleetGenerationFailed', { message: error instanceof Error ? error.message : t('populationAnalysis.unknownError') }),
          severity: 'error',
          icon: <Icon size={20} name="triangle-exclamation" style={{ marginRight: 8, color: '#d32f2f' }} />
        });
      }
      return undefined;
    }
  };

  // Handler: Generate both trips and fleet
  const handleGenerateBoth = () => {
    if (!global || !selectedPolygon) {
      setSnackbar({
        open: true,
        message: t('populationAnalysis.errorMissingContextGeneric'),
        severity: 'error',
        icon: <Icon size={20} name="triangle-exclamation" style={{ marginRight: 8, color: '#d32f2f' }} />
      });
      return;
    }

    try {
      const tripsCount = parseDisplayCount(dailyTripsDisplay);
      const vehiclesCount = parseDisplayCount(vehiclesNeededDisplay);

      const newLayerId = handleGenerateTrips(true);
      const newFleetId = handleGenerateFleet(true);

      // Only show success if both operations succeeded
      if (newLayerId !== undefined && newFleetId !== undefined) {
        setSnackbar({
          open: true,
          message: t('populationAnalysis.generatedBothSuccess', { trips: tripsCount, vehicles: vehiclesCount }),
          severity: 'success',
          icon: <Icon size={20} d={ICON_FALLBACKS.AllInclusive} style={{ marginRight: 8, color: '#2e7d32' }} />
        });
      } else {
        // Individual error messages are already shown by handleGenerateTrips/handleGenerateFleet
        // Just show a summary if one failed
        const failedOps: string[] = [];
        if (newLayerId === undefined) failedOps.push(t('populationAnalysis.trips'));
        if (newFleetId === undefined) failedOps.push(t('populationAnalysis.fleet'));
        setSnackbar({
          open: true,
          message: t('populationAnalysis.generateFailedSummary', { ops: failedOps.join(` ${t('populationAnalysis.and')} `) }),
          severity: 'warning',
          icon: <Icon size={20} name="triangle-exclamation" style={{ marginRight: 8, color: '#ed6c02' }} />
        });
      }
    } catch (error) {
      console.error('Error in handleGenerateBoth:', error);
      setSnackbar({
        open: true,
        message: t('populationAnalysis.generateFailedGeneric', { message: error instanceof Error ? error.message : t('populationAnalysis.unknownError') }),
        severity: 'error',
        icon: <Icon size={20} name="triangle-exclamation" style={{ marginRight: 8, color: '#d32f2f' }} />
      });
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} 
      variant="persistent"
      PaperProps={{ 
        sx: (theme) => ({
          width: 'calc(100% - 65px)',
          maxWidth: 400,
          p: 2,
          pr: 2, // keep room for rail but avoid wasted empty area
          right: 65,
          height: 'calc(100vh - 80px)', // Adjust height to account for logo
          top: 80, // Position below logo
          overflowY: 'auto',
          ...(theme.palette.surface ? frostedDrawerPaperSx(theme) : {
            backgroundColor: opsColors.neutral.panel,
            border: '1px solid rgba(58, 58, 72, 0.12)',
            boxShadow: opsElevation.drawer.shadow,
          }),
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          // Avoid overriding `overflowY: auto` (previously blocked scrolling + could clip popovers)
          overflowX: 'hidden',
          zIndex: 2201,
        })
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
        sx={(theme) => ({
          px: 1.5,
          py: 0.75,
          borderRadius: '12px',
          ...(theme.palette.surface ? frostedDrawerHeaderSx(theme, 0.22) : {
            backgroundColor: 'rgba(255, 255, 255, 0.22)',
            borderBottom: '1px solid rgba(58, 58, 72, 0.12)',
          }),
        })}
      >
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', pr: 1 }}
        >
          {t('populationAnalysis.title')}
        </Typography>
        <IconButton onClick={onClose}><Icon size={20} name="xmark" /></IconButton>
      </Box>
      <Divider sx={{ mb: 1 }} />
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="polygon-select-label">{t('populationAnalysis.selectPolygon')}</InputLabel>
        <Select
          labelId="polygon-select-label"
          value={selectedPolygonId || ''}
          label={t('populationAnalysis.selectPolygon')}
          MenuProps={{
            // Keep menu above high-zIndex persistent drawer
            sx: { zIndex: 2305 },
            PaperProps: {
              sx: {
                zIndex: 2306,
                borderRadius: '12px',
                border: '1px solid',
                borderColor: 'surface.borderSubtle',
                boxShadow: 3,
                mt: 0.5,
                '& .MuiMenuItem-root': {
                  borderRadius: 1,
                  mx: 0.5,
                  my: 0.25,
                },
                '& .MuiMenuItem-root.Mui-selected': {
                  backgroundColor: 'actionBrand.primary',
                  color: '#fff',
                },
                '& .MuiMenuItem-root.Mui-selected:hover': {
                  backgroundColor: 'actionBrand.primaryHover',
                },
              }
            }
          }}
          open={isPolygonSelectOpen}
          onOpen={() => {
            if (hasPolygons) {
              setIsPolygonSelectOpen(true);
            }
          }}
          onClose={() => setIsPolygonSelectOpen(false)}
          onChange={e => handlePolygonChange(e.target.value as string)}
          disabled={!hasPolygons}
          sx={{
            borderRadius: '12px',
            backgroundColor: 'surface.primary',
          }}
        >
          {polygonShapes.map(polygon => (
            <MenuItem key={polygon.id} value={polygon.id}>
              {polygon._layerName ? `${polygon._layerName}: ` : ''}{polygon.name || polygon.id}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {!hasPolygons && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('populationAnalysis.drawPrompt')}
        </Typography>
      )}
      {selectedPolygon ? (
        <Box>
          {selectedPolygon.totalPopulation == null && (
            <Box mb={2} display="flex" justifyContent="center">
              <Button
                variant="contained"
                size="large"
                disabled={loadingPop}
                startIcon={
                  loadingPop ? (
                    <CircularProgress
                      size={20}
                      sx={{
                        color: 'white',
                        '& .MuiCircularProgress-circle': {
                          strokeLinecap: 'round',
                        }
                      }}
                    />
                  ) : (
                    <Icon size={20} d={ICON_FALLBACKS.DataUsage} />
                  )
                }
                onClick={async () => {
                  if (!selectedPolygonId) return;
                  const validationError = onValidatePopulationPolygon?.(selectedPolygonId) ?? null;
                  if (validationError) {
                    setPopulationPolygonValidationError(validationError);
                    return;
                  }
                  setPopulationPolygonValidationError(null);
                  setLoadingPop(true);
                  
                  // Track population data fetch initiation
                  logEvent({
                    category: 'Analysis',
                    action: 'PopulationDataFetchInitiated',
                    label: 'Population Data Fetch Started',
                    polygonId: selectedPolygonId,
                    polygonName: selectedPolygon?.name || 'Unnamed'
                  });
                  
                  try {
                    await onGetPopulationData(selectedPolygonId);
                    
                    // Track successful population data fetch
                    logEvent({
                      category: 'Analysis',
                      action: 'PopulationDataFetchSuccess',
                      label: 'Population Data Fetch Completed',
                      polygonId: selectedPolygonId,
                      polygonName: selectedPolygon?.name || 'Unnamed'
                    });
                  } catch (error) {
                    // Track population data fetch failure
                    logEvent({
                      category: 'Analysis',
                      action: 'PopulationDataFetchError',
                      label: 'Population Data Fetch Failed',
                      polygonId: selectedPolygonId,
                      polygonName: selectedPolygon?.name || 'Unnamed',
                      error: error instanceof Error ? error.message : 'Unknown error'
                    });
                  } finally {
                    setLoadingPop(false);
                  }
                }}
                sx={(theme) => ({
                  py: 1.2,
                  px: 2.25,
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  borderRadius: '10px',
                  color: '#ffffff',
                  backgroundColor: opsColors.brand.primary,
                  '&:hover': {
                    backgroundColor: opsColors.brand.primaryHover,
                  },
                })}
              >
                {loadingPop ? t('populationAnalysis.analyzing') : t('populationAnalysis.getPopulationData')}
              </Button>
            </Box>
          )}
          {populationPolygonValidationError ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {populationPolygonValidationError}
            </Alert>
          ) : null}
          {transitError ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {transitError.includes('OSM API failed across all endpoints')
                ? t('populationAnalysis.transitUnavailable')
                : transitError}
            </Alert>
          ) : null}
          <Box display="flex" gap={1} mb={2} flexWrap="wrap" aria-live="polite">
            <Paper elevation={1} sx={{ ...sectionCardSx, flex: 1, textAlign: 'center', minHeight: 168, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
              <Icon size={32} name="users" style={{ marginBottom: 8 }} />
              <Typography variant="subtitle2" color="text.secondary">{t('populationAnalysis.totalPopulation')}</Typography>
              <Typography variant="h5" fontWeight={700} color="primary.main">
                {selectedPolygon.totalPopulation?.toLocaleString() ?? '--'}
              </Typography>
            </Paper>
            <Paper elevation={1} sx={{ ...sectionCardSx, flex: 1, textAlign: 'center', minHeight: 168, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
              <Icon size={32} name="bus" style={{ marginBottom: 8 }} />
              <Typography variant="subtitle2" color="text.secondary">{t('populationAnalysis.coveredByTransit')}</Typography>
              {transitLoading && selectedPolygon.coveredPopulation == null ? (
                <CircularProgress size={20} sx={{ mt: 0.5, color: 'success.main' }} aria-label={t('populationAnalysis.loadingCovered')} />
              ) : (
                <Typography variant="h5" fontWeight={700} color="success.main">
                  {selectedPolygon.coveredPopulation?.toLocaleString() ?? '--'}
                </Typography>
              )}
            </Paper>
            <Paper elevation={1} sx={{ ...sectionCardSx, flex: 1, textAlign: 'center', minHeight: 168, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
              <Icon size={32} name="triangle-exclamation" style={{ marginBottom: 8 }} />
              <Typography variant="subtitle2" color="text.secondary">{t('populationAnalysis.inTransitDesert')}</Typography>
              {transitLoading && selectedPolygon.desertPopulation == null ? (
                <CircularProgress size={20} sx={{ mt: 0.5, color: 'error.main' }} aria-label={t('populationAnalysis.loadingDesert')} />
              ) : (
                <Typography variant="h5" fontWeight={700} color="error.main">
                  {selectedPolygon.desertPopulation?.toLocaleString() ?? '--'}
                </Typography>
              )}
            </Paper>
          </Box>
          <Paper elevation={1} sx={{ ...sectionCardSx, mb: 2 }}>
          {/* On-Demand Analysis Sliders */}
          <Box mb={3} sx={{ width: '100%', mx: 'auto' }}>
            <Typography variant="h5" color="text.primary" sx={{ mb: 1.5, mt: 0.5, fontWeight: 700, textAlign: 'left' }}>{t('populationAnalysis.flexibility')}</Typography>
            <Slider
              value={flexibility}
              onChange={(_, v) => {
                const snapped = snapToStop(v as number, flexibilityStops, 1.5);
                const oldValue = flexibility;
                setFlexibility(snapped);
                
                // Track flexibility slider change
                logEvent({
                  category: 'Analysis',
                  action: 'FlexibilityChanged',
                  label: 'Flexibility Slider Adjusted',
                  polygonId: selectedPolygonId || 'none',
                  oldValue: oldValue,
                  newValue: snapped,
                  flexibilityLevel: getFlexibilityLevel(snapped)
                });
              }}
              step={1}
              marks={flexibilityMarks}
              min={30}
              max={70}
              valueLabelDisplay="off"
              sx={{
                mt: 2, mb: 2,
                '.MuiSlider-markLabel': {
                  fontSize: 13,
                  whiteSpace: 'normal',
                  maxWidth: 80,
                  textAlign: 'center',
                  lineHeight: 1.2,
                  fontWeight: 500,
                },
                '.MuiSlider-markLabel[data-index="0"]': { paddingLeft: '6px' },
                '.MuiSlider-markLabel[data-index="4"]': { paddingRight: '6px' },
                '.MuiSlider-valueLabel': {
                  fontSize: 14,
                  fontWeight: 700,
                }
              }}
            />
          </Box>
          <Divider sx={{ my: 1.5, width: '100%', mx: 'auto' }} />
          <Box mb={0.5} sx={{ width: '100%', mx: 'auto' }}>
            <Typography variant="h5" color="text.primary" sx={{ mb: 1.5, mt: 1, fontWeight: 700, textAlign: 'left' }}>{t('populationAnalysis.demand')}</Typography>
            <Slider
              value={demandPct}
              onChange={(_, v) => {
                const snapped = snapToStop(v as number, demandStops, 0.01);
                const oldValue = demandPct;
                setDemandPct(snapped);
                
                // Track demand slider change
                logEvent({
                  category: 'Analysis',
                  action: 'DemandChanged',
                  label: 'Demand Slider Adjusted',
                  polygonId: selectedPolygonId || 'none',
                  oldValue: oldValue,
                  newValue: snapped,
                  demandLevel: getDemandLevel(snapped),
                  demandPercentage: Math.round(snapped * 100)
                });
              }}
              step={0.01}
              marks={demandMarks}
              min={0.10}
              max={0.40}
              valueLabelDisplay="off"
              valueLabelFormat={v => `${Math.round((v as number) * 100)}%`}
              sx={{
                mt: 2, mb: 2,
                '.MuiSlider-markLabel': {
                  fontSize: 13,
                  whiteSpace: 'normal',
                  maxWidth: 80,
                  textAlign: 'center',
                  lineHeight: 1.2,
                  fontWeight: 500,
                },
                '.MuiSlider-markLabel[data-index="0"]': { paddingLeft: '6px' },
                '.MuiSlider-markLabel[data-index="4"]': { paddingRight: '6px' },
                '.MuiSlider-valueLabel': {
                  fontSize: 14,
                  fontWeight: 700,
                }
              }}
            />
          </Box>
          </Paper>
          <Box display={{ xs: 'block', sm: 'flex' }} gap={1} mb={2} alignItems="stretch">
            <Paper elevation={1} sx={{ ...sectionCardSx, flex: 1, p: 1.5, textAlign: 'center', minWidth: 120, minHeight: 80, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', mb: { xs: 1, sm: 0 } }}>
              <Typography variant="subtitle2" color="text.secondary" fontSize={14}>{t('populationAnalysis.dailyTrips')}</Typography>
              <Typography variant="h5" fontWeight={700} color="primary.main" fontSize={24}>
                {dailyTripsDisplay}
              </Typography>
            </Paper>
            <Paper elevation={1} sx={{ ...sectionCardSx, flex: 1, p: 1.5, textAlign: 'center', minWidth: 120, minHeight: 80, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2" color="text.secondary" fontSize={14}>{t('populationAnalysis.vehiclesRecommended')}</Typography>
              <Typography variant="h5" fontWeight={700} color="success.main" fontSize={24}>
                {vehiclesNeededDisplay}
              </Typography>
            </Paper>
          </Box>
          <Divider sx={{ mb: 1 }} />
          
          {/* --- BEGIN: Intelligent Generation Buttons --- */}
          {(() => {
            // Helper variables for button enable/disable logic
            const hasPopulation = !!selectedPolygon?.totalPopulation && selectedPolygon.totalPopulation > 0;
            const hasTripsEstimate = hasPopulation && dailyTripsDisplay !== '--' && !isNaN(Number(dailyTripsDisplay.replace(/[^\d]/g, '')));
            const hasFleetEstimate = hasPopulation && vehiclesNeededDisplay !== '--' && !isNaN(Number(vehiclesNeededDisplay.replace(/[^\d]/g, '')));
            const canGenerateBoth = hasTripsEstimate && hasFleetEstimate;
            const isBusy = generating !== null;
            const tripsEnabled = hasTripsEstimate && !isBusy;
            const fleetEnabled = hasFleetEstimate && !isBusy;
            const bothEnabled = canGenerateBoth && !isBusy;

            // Run a synchronous generation handler with a UI in-progress indicator.
            // Defers the CPU-bound work one animation frame so React can paint the
            // spinner state before the main thread is blocked.
            const runGeneration = (mode: 'trips' | 'fleet' | 'both', fn: () => void) => {
              setGenerating(mode);
              requestAnimationFrame(() => {
                try {
                  fn();
                } finally {
                  setGenerating(null);
                }
              });
            };

            // Disabled cards get a tooltip explaining the prerequisite. ButtonBase
            // gives us keyboard focus/Enter activation and ARIA semantics for free.
            const disabledTip = !hasPopulation ? t('populationAnalysis.getPopulationFirst') : '';
            const renderCard = (opts: {
              mode: 'trips' | 'fleet' | 'both';
              enabled: boolean;
              label: string;
              onClick: () => void;
              icon: React.ReactNode;
            }) => {
              const busy = generating === opts.mode;
              const card = (
                <ButtonBase
                  disabled={!opts.enabled}
                  onClick={opts.onClick}
                  focusRipple
                  aria-label={opts.label}
                  sx={{
                    flex: 1,
                    border: '1px solid',
                    borderColor: 'actionBrand.outlinedBorder',
                    borderRadius: 2,
                    backgroundColor: opts.enabled ? 'surface.primary' : 'surface.secondary',
                    transition: 'all 0.2s',
                    p: 2,
                    minWidth: 0,
                    boxShadow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: opts.enabled ? 1 : 0.5,
                    '&:hover': opts.enabled ? {
                      borderColor: 'actionBrand.primary',
                      backgroundColor: 'surface.secondary',
                      boxShadow: 2,
                      '.gen-title, .gen-icon': {
                        color: 'actionBrand.primary !important',
                      }
                    } : {},
                  }}
                >
                  {busy ? (
                    <CircularProgress size={24} sx={{ color: 'actionBrand.primary', mb: 1 }} aria-label={`${opts.label} in progress`} />
                  ) : (
                    opts.icon
                  )}
                  <Typography className="gen-title" variant="subtitle1" fontWeight={700} sx={{ textAlign: 'center', color: 'text.primary', mb: 0.5 }}>
                    {opts.label}
                  </Typography>
                </ButtonBase>
              );
              return !opts.enabled && disabledTip
                ? <Tooltip title={disabledTip} key={opts.mode}><span style={{ flex: 1, display: 'flex' }}>{card}</span></Tooltip>
                : <React.Fragment key={opts.mode}>{card}</React.Fragment>;
            };

            return (
              <Box mt={2} mb={2} display="flex" flexDirection="column" alignItems="center">
                <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                  {renderCard({
                    mode: 'trips',
                    enabled: tripsEnabled,
                    label: t('populationAnalysis.generateTrips'),
                    onClick: () => runGeneration('trips', () => handleGenerateTrips()),
                    icon: <Icon size={34} d={ICON_FALLBACKS.AltRoute} className="gen-icon" style={{ color: '#ef7335', marginBottom: 8 }} />,
                  })}
                  {renderCard({
                    mode: 'fleet',
                    enabled: fleetEnabled,
                    label: t('populationAnalysis.generateFleet'),
                    onClick: () => runGeneration('fleet', () => handleGenerateFleet()),
                    icon: <Icon size={34} name="bus" className="gen-icon" style={{ color: '#ef7335', marginBottom: 8 }} />,
                  })}
                  {renderCard({
                    mode: 'both',
                    enabled: bothEnabled,
                    label: t('populationAnalysis.generateBoth'),
                    onClick: () => runGeneration('both', () => handleGenerateBoth()),
                    icon: <Icon size={34} d={ICON_FALLBACKS.AllInclusive} className="gen-icon" style={{ color: '#ef7335', marginBottom: 8 }} />,
                  })}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  {t('populationAnalysis.generationHint')}
                </Typography>
              </Box>
            );
          })()}
          {/* --- END: Intelligent Generation Buttons --- */}

          {/* Advanced Settings Accordion */}
            <Accordion 
              expanded={advancedSettingsExpanded} 
              onChange={() => setAdvancedSettingsExpanded(!advancedSettingsExpanded)}
              sx={(theme) => ({ 
                mb: 2, 
                boxShadow: opsElevation.raised.shadow,
                border: `1px solid ${opsColors.neutral.line}`,
                '&:before': { display: 'none' },
                '&.Mui-expanded': { margin: '8px 0' }
              })}
            >
              <AccordionSummary 
                expandIcon={<Icon size={20} name="chevron-down" />}
                sx={{ 
                  backgroundColor: 'surface.secondary',
                  '&:hover': { backgroundColor: 'surface.muted' },
                  '.adv-settings-title, .adv-settings-icon': {
                    color: 'actionBrand.primary !important',
                  }
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Icon size={20} d={ICON_FALLBACKS.Timeline} className="adv-settings-icon" />
                  <Typography className="adv-settings-title" variant="h6" fontWeight={600}>
                    {t('populationAnalysis.advancedSettings')}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 2 }}>
                <Box display="flex" flexDirection="column" gap={3}>
                  {/* Time Settings */}
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} mb={2} display="flex" alignItems="center" gap={1}>
                      <Icon name="clock" size={20} />
                      {t('populationAnalysis.serviceHours')}
                    </Typography>
                    <Box display="flex" gap={2} alignItems="center">
                      <TextField
                        label={t('populationAnalysis.startTime')}
                        type="time"
                        size="small"
                        fullWidth
                        value={startTime.toTimeString().slice(0, 5)}
                        onChange={(event) => {
                          const timeValue = event.target.value;
                          if (timeValue) {
                            const [hours, minutes] = timeValue.split(':');
                            const newDate = createWallTimeDate(DEFAULT_SIMULATION_DATE, timeValue);

                            // Validate that start time is before end time
                            const startMinutes = parseInt(hours) * 60 + parseInt(minutes);
                            const endMinutes = endTime.getHours() * 60 + endTime.getMinutes();
                            
                            if (startMinutes >= endMinutes) {
                              setSnackbar({
                                open: true,
                                message: t('populationAnalysis.errorStartBeforeEnd'),
                                severity: 'warning',
                                icon: <Icon size={20} name="triangle-exclamation" style={{ marginRight: 8, color: '#ed6c02' }} />
                              });
                              return;
                            }
                            
                            setStartTime(newDate);
                            logEvent({
                              category: 'Advanced Settings',
                              action: 'StartTimeChanged',
                              label: 'Start Time Updated',
                              newTime: timeValue
                            });
                          }
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                      <Typography variant="body2" color="text.secondary">{t('populationAnalysis.to')}</Typography>
                      <TextField
                        label={t('populationAnalysis.endTime')}
                        type="time"
                        size="small"
                        fullWidth
                        value={endTime.toTimeString().slice(0, 5)}
                        onChange={(event) => {
                          const timeValue = event.target.value;
                          if (timeValue) {
                            const [hours, minutes] = timeValue.split(':');
                            const newDate = createWallTimeDate(DEFAULT_SIMULATION_DATE, timeValue);

                            // Validate that end time is after start time
                            const endMinutes = parseInt(hours) * 60 + parseInt(minutes);
                            const startMinutes = startTime.getHours() * 60 + startTime.getMinutes();
                            
                            if (endMinutes <= startMinutes) {
                              setSnackbar({
                                open: true,
                                message: t('populationAnalysis.errorEndAfterStart'),
                                severity: 'warning',
                                icon: <Icon size={20} name="triangle-exclamation" style={{ marginRight: 8, color: '#ed6c02' }} />
                              });
                              return;
                            }
                            
                            setEndTime(newDate);
                            logEvent({
                              category: 'Advanced Settings',
                              action: 'EndTimeChanged',
                              label: 'End Time Updated',
                              newTime: timeValue
                            });
                          }
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Peakiness Profile */}
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} mb={2} display="flex" alignItems="center" gap={1}>
                      <Icon d={ICON_FALLBACKS.Timeline} size={20} />
                      {t('populationAnalysis.demandPattern')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      {Object.entries(peakinessProfiles).map(([key, profile]) => {
                        const iconPath = profile.icon;
                        const selected = peakinessProfile === key;
                        const highlightColor = 'actionBrand.primary';
                        const defaultTextColor = 'text.primary';
                        const defaultDescColor = 'text.secondary';
                        return (
                          <ButtonBase
                            key={key}
                            focusRipple
                            role="radio"
                            aria-checked={selected}
                            aria-label={t('populationAnalysis.patternAria', { name: profile.name })}
                            sx={{
                              flex: 1,
                              border: selected ? `2px solid` : '1px solid',
                              borderColor: selected ? highlightColor : 'surface.borderSubtle',
                              borderRadius: 2,
                              backgroundColor: selected ? 'surface.secondary' : 'surface.primary',
                              transition: 'all 0.2s',
                              p: 2,
                              minWidth: 0,
                              boxShadow: selected ? 2 : 0,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              '&:hover': {
                                borderColor: highlightColor,
                                backgroundColor: 'surface.secondary',
                                boxShadow: 2,
                                '.demand-title, .demand-icon, .demand-desc': {
                                  color: highlightColor + ' !important',
                                }
                              }
                            }}
                            onClick={() => {
                              if (selected) {
                                setPeakinessProfile('');
                                logEvent({
                                  category: 'Advanced Settings',
                                  action: 'PeakinessProfileUnselected',
                                  label: 'Peakiness Profile Unselected',
                                  oldProfile: key
                                });
                              } else {
                                setPeakinessProfile(key);
                                logEvent({
                                  category: 'Advanced Settings',
                                  action: 'PeakinessProfileChanged',
                                  label: 'Peakiness Profile Updated',
                                  newProfile: key
                                });
                              }
                            }}
                          >
                            <Typography className="demand-title" variant="subtitle1" fontWeight={700} sx={{ textAlign: 'left', mb: 1, color: selected ? highlightColor : defaultTextColor }}>
                              {profile.name}
                            </Typography>
                            <Icon className="demand-icon" d={iconPath} size={24} style={{ color: selected ? '#ef7335' : '#6b7280', marginBottom: 8 }} />
                            <Typography className="demand-desc" variant="body2" sx={{ fontSize: '0.95rem', minHeight: 40, textAlign: 'left', color: selected ? highlightColor : defaultDescColor }}>
                              {profile.description}
                            </Typography>
                          </ButtonBase>
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>

          <Box sx={{ minHeight: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', flexDirection: 'column' }}>
            <Typography variant="body2">{t('populationAnalysis.estimatesHint')}</Typography>
            <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', opacity: 0.8, mt: 1 }}>
              {t('populationAnalysis.advancedHint')}
            </Typography>
          </Box>
        </Box>
      ) : hasPolygons ? (
        <Typography variant="body1" color="text.secondary">{t('populationAnalysis.selectPolygonHint')}</Typography>
      ) : null}
      {/* Add Snackbar JSX at the bottom of the drawer */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            bgcolor: opsColors.neutral.panel,
            border: `1px solid ${opsColors.neutral.line}`,
            borderRadius: 2,
            boxShadow: opsElevation.floating.shadow,
            px: 2,
            py: 1.5,
            minWidth: 340,
            maxWidth: 480,
            width: '100%',
            gap: 2
          })}
        >
          {/* Icon left */}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
            {snackbar.icon}
          </Box>
          {/* Message center */}
          <Box sx={{ flex: 1, color: 'text.primary', fontWeight: 600, fontSize: '1rem', textAlign: 'left' }}>
            {snackbar.message}
          </Box>
          {/* Logo right */}
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
            <img src={`${process.env.PUBLIC_URL}/Liftango_Logo.svg`} alt={t('populationAnalysis.logoAlt')} style={{ height: 32, width: 'auto', marginLeft: 8 }} />
          </Box>
        </Box>
      </Snackbar>
    </Drawer>
  );
};

export default PopulationAnalysisDrawer; 
